import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { SlidersHorizontal } from "lucide-react";
import api from "@/api";
import { useLang } from "@/i18n";
import Reveal from "@/components/Reveal";

export default function PropertyGallery() {
  const { t } = useLang();
  const [properties, setProperties] = useState([]);
  const [guests, setGuests] = useState("");

  useEffect(() => {
    api.get("/properties").then((r) => setProperties(r.data)).catch(() => {});
  }, []);

  const filtered = properties
    .filter((p) => !guests || p.guests >= Number(guests))
    .slice(0, 4);

  return (
    <div data-testid="property-gallery-home" className="mt-16 sm:mt-20">
      <div className="flex items-center gap-3">
        <SlidersHorizontal size={18} strokeWidth={1.25} className="text-maquis-olive" />
        <select
          data-testid="gallery-guests-filter"
          value={guests}
          onChange={(e) => setGuests(e.target.value)}
          className="h-11 rounded-full border border-maquis-deep/20 bg-transparent px-4 text-sm text-maquis-deep outline-none transition-colors focus:border-maquis-olive"
        >
          <option value="">{t.gallery.filter_all}</option>
          {[2, 4, 6, 8, 10].map((n) => (
            <option key={n} value={n}>{n}+ {t.search.guests.toLowerCase()}</option>
          ))}
        </select>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-3" data-testid="gallery-grid">
        {filtered.map((p, i) => {
          const weeklies = [p.weekly_price, ...(p.price_periods || []).filter((x) => x.per === "week").map((x) => x.price)].filter((v) => v != null && v > 0);
          const minPrice = weeklies.length ? Math.min(...weeklies) : null;
          return (
          <Reveal key={p.id} delay={(i % 4) * 0.06}>
            <Link
              to={`/locations/${p.slug}`}
              data-testid={`gallery-card-${p.slug}`}
              className="group block rounded-md bg-white p-4 shadow-[0_10px_30px_-16px_rgba(42,51,35,0.25)] transition-shadow duration-300 hover:shadow-[0_18px_44px_-18px_rgba(42,51,35,0.35)]"
            >
              <h3 className="pb-3 font-serif italic font-semibold text-xl sm:text-2xl text-maquis-deep">{p.name}</h3>
              <div className="overflow-hidden rounded-sm">
                <img
                  src={p.cover_image || p.images[0]}
                  alt={p.name}
                  loading="lazy"
                  className="h-56 w-full object-cover sm:h-64 transition-transform duration-[1.2s] ease-out group-hover:scale-105"
                />
              </div>
              <dl className="mt-4 space-y-1 text-xs sm:text-sm text-maquis-olive">
                <div className="flex gap-1">
                  <dt className="font-semibold text-maquis-deep">{t.gallery.place} :</dt>
                  <dd>{p.location.split(",")[0]}</dd>
                </div>
                <div className="flex gap-1">
                  <dt className="font-semibold text-maquis-deep">{t.gallery.surface} :</dt>
                  <dd>{p.surface ? `${p.surface} m²` : "—"}</dd>
                </div>
                <div className="flex gap-1">
                  <dt className="font-semibold text-maquis-deep">{t.gallery.people} :</dt>
                  <dd>2-{p.guests}</dd>
                </div>
                <div className="flex gap-1">
                  <dt className="font-semibold text-maquis-deep">{t.gallery.rooms} :</dt>
                  <dd>{p.bedrooms}</dd>
                </div>
              </dl>
              {minPrice && (
                <p data-testid={`gallery-price-${p.slug}`} className="mt-3 text-sm font-semibold text-maquis-deep">
                  {t.gallery.price_from} {minPrice.toLocaleString("fr-FR")} € / sem.
                </p>
              )}
            </Link>
          </Reveal>
          );
        })}
      </div>

      <div className="mt-12 text-center">
        <Link
          to="/locations"
          data-testid="gallery-discover-all"
          className="btn-outline-elegant text-maquis-deep hover:bg-maquis-deep hover:text-casse"
        >
          {t.gallery.discover_all}
        </Link>
      </div>
    </div>
  );
}
